import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Money as CashIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  CheckCircle as SuccessIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  RequestQuote as ChequeIcon
} from '@mui/icons-material';

/**
 * Purchase order completion component
 * @param {Object} props Component props
 * @param {Object} props.totals Order totals object (subtotal, taxAmount, total)
 * @param {Object} props.supplier Selected supplier object (optional)
 * @param {Function} props.onCompletePurchase Callback when purchase is completed
 * @param {boolean} props.loading Whether purchase is being processed
 * @param {boolean} props.orderCompleted Whether order has been completed
 * @returns {JSX.Element} Purchase order completion component
 */
const POPayment = ({ 
  totals, 
  supplier, 
  onCompletePurchase, 
  loading = false, 
  orderCompleted = false
}) => {
  const theme = useTheme();
  const [paymentMethod, setPaymentMethod] = useState('CREDIT_CARD');
  const [notes, setNotes] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [estimatedDelivery, setEstimatedDelivery] = useState('');

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  // Format date as YYYY-MM-DD
  const formatDate = (date) => {
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  // Get today's date in YYYY-MM-DD format
  const today = formatDate(new Date());

  // Handle purchase order submission
  const handleSubmitPurchase = () => {
    setShowSuccess(true);
    
    // Prepare purchase data
    const purchaseData = {
      method: paymentMethod,
      estimatedDelivery: estimatedDelivery || null,
      notes: notes
    };
    
    // Call the parent callback
    onCompletePurchase(purchaseData);
  };

  // Payment method icon mapping
  const paymentIcons = {
    CREDIT_CARD: <CreditCardIcon />,
    CHEQUE: <ChequeIcon />,
    PAYNOW: <PaymentIcon />,
    CASH: <CashIcon />,
    CREDIT: <ReceiptIcon />
  };

  return (
    <Box sx={{ width: '100%' }}>
      {orderCompleted || showSuccess ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <SuccessIcon sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Purchase Order Submitted!
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            The purchase order has been created successfully.
          </Typography>
          <Typography variant="body2" color="primary" sx={{ mt: 1, fontStyle: 'italic' }}>
            Inventory has been updated to reflect this purchase.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Supplier Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Supplier Information
            </Typography>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                {supplier ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Company Name
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {supplier.companyName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Contact
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {supplier.contactName || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Phone
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {supplier.phone || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Email
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {supplier.email || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body1" align="center" color="textSecondary">
                    No supplier selected
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Order Summary */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Order Summary
            </Typography>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Subtotal
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" align="right">
                      {formatCurrency(totals.subtotal)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">
                      Tax
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" align="right">
                      {formatCurrency(totals.taxAmount)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Total
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle1" fontWeight="bold" align="right">
                      {formatCurrency(totals.total)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Delivery Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Delivery Information
            </Typography>
            <Card variant="outlined">
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Estimated Delivery Date"
                      type="date"
                      fullWidth
                      value={estimatedDelivery}
                      onChange={(e) => setEstimatedDelivery(e.target.value)}
                      inputProps={{ min: today }}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ShippingIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Payment Method Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Payment Method
            </Typography>
            <FormControl component="fieldset" disabled={loading}>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                row
              >
                <FormControlLabel
                  value="CREDIT_CARD"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CreditCardIcon sx={{ mr: 1 }} />
                      Credit Card
                    </Box>
                  }
                />
                <FormControlLabel
                  value="CHEQUE"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ChequeIcon sx={{ mr: 1 }} />
                      Cheque
                    </Box>
                  }
                />
                <FormControlLabel
                  value="PAYNOW"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PaymentIcon sx={{ mr: 1 }} />
                      UPI
                    </Box>
                  }
                />
                <FormControlLabel
                  value="CASH"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CashIcon sx={{ mr: 1 }} />
                      Cash
                    </Box>
                  }
                />
                <FormControlLabel
                  value="CREDIT"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ReceiptIcon sx={{ mr: 1 }} />
                      Credit
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Notes */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Add any notes or instructions for the supplier..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
            />
          </Box>
          
          {/* Submit Button */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={loading || !supplier}
            onClick={handleSubmitPurchase}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Processing...' : 'Submit Purchase Order'}
          </Button>
          
          {!supplier && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Please select a supplier before submitting
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default POPayment; 
